import { getFeatureFlag, isBoolean } from "@sinch/feature-flags";

import type { HiveConfig } from "../hooks/useHiveConfig";
import { loadMFE } from "../mfe/mfeLoaders";
import type {
  IsRouteEnabledOptions,
  RouteConfig,
  RouteConfigBase,
  RouteConfigMenu,
  RouteConfigSubMenu,
} from "./types/navigationConfigTypes";
import { getNewUniqueRouteKey } from "./utils/navigationConfigUtils";

const isHiveFeatureOn = (
  hiveConfigs: Record<string, HiveConfig[number]["config_value"]> | undefined,
  featureName: string
): boolean => {
  const feature = hiveConfigs?.[featureName];
  if (!feature) {
    // We consider feature "on" by default
    return true;
  }
  return feature !== "0";
};

const homeRoute = {
  key: getNewUniqueRouteKey("home"),
  path: "/",
  mfe: { name: "Home", entry: "MainEntry" },
  menu: {
    icon: "branded-home",
    textKey: "menuItemHome",
    href: "/",
    subRoutes: [
      {
        key: getNewUniqueRouteKey("home"),
        path: "/home/*",
        mfe: { name: "Home", entry: "MainEntry" },
        href: "/home/",
      },
    ],
  },
} satisfies RouteConfigMenu;

const conversationsWorkspaceSubRoute = {
  key: getNewUniqueRouteKey("conv-workspace"),
  path: "/conversations/workspace/*",
  textKey: "menuItemWorkspace",
  href: "/conversations/workspace",
  allowedPermissions: ["ticketRead", "chatRead"],
  isEnabled: ({ hiveConfig }) => isHiveFeatureOn(hiveConfig, "feature_contact"),
} satisfies RouteConfigSubMenu;

const conversationsAssignmentSubRoute = {
  key: getNewUniqueRouteKey("conv-assign"),
  path: "/conversations/assignment/*",
  textKey: "menuItemAssignment",
  href: "/conversations/assignment",
  allowedPermissions: ["ticketShowAssignment"],
  isEnabled: ({ hiveConfig }) => isHiveFeatureOn(hiveConfig, "feature_contact"),
} satisfies RouteConfigSubMenu;

const conversationsRoute = {
  key: getNewUniqueRouteKey("conversations"),
  path: "/conversations/*",
  mfe: { name: "Contact", entry: "Container" },
  allowedPermissions: ["ticketRead", "chatRead"],
  isEnabled: ({ hiveConfig }) => isHiveFeatureOn(hiveConfig, "feature_contact"),
  menu: {
    icon: "branded-contact",
    textKey: "menuItemConversations",
    href: "/conversations/workspace",
    subRoutes: [
      conversationsWorkspaceSubRoute,
      conversationsAssignmentSubRoute,
      {
        key: getNewUniqueRouteKey("conv-all"),
        path: "/conversations/conversations/*",
        textKey: "menuItemAllConversations",
        href: "/conversations/conversations",
        isEnabled: ({ hiveConfig }) =>
          isHiveFeatureOn(hiveConfig, "feature_contact"),
      },
      {
        key: getNewUniqueRouteKey("conv-settings"),
        path: "/conversations/settings/*",
        textKey: "menuItemConversationsSettings",
        href: "/conversations/settings",
        allowedPermissions: ["administrate"],
        isEnabled: ({ hiveConfig }) =>
          isHiveFeatureOn(hiveConfig, "feature_contact"),
      },
    ],
  },
} satisfies RouteConfigMenu;

const chatbotRoute = {
  key: getNewUniqueRouteKey("chatbot"),
  path: "/chatbot/*",
  mfe: { name: "BotBuilder", entry: "Container" },
  allowedPermissions: ["botEdit", "botEditStatus", "botRemove"],
  isEnabled: ({ hiveConfig }) =>
    !getFeatureFlag("chatlayerBotBuilderV2", isBoolean, false) &&
    isHiveFeatureOn(hiveConfig, "feature_chatbot"),
  menu: {
    icon: "branded-chatbot",
    textKey: "menuItemChatbot",
    href: "/chatbot/overview",
  },
} satisfies RouteConfigMenu;

const chatbotV2Route = {
  key: getNewUniqueRouteKey("chatbotV2"),
  path: "/chatbot/*",
  mfe: { name: "BotBuilderV2", entry: "Container" },
  allowedPermissions: ["botEdit", "botEditStatus", "botRemove"],
  isEnabled: ({ hiveConfig }) =>
    getFeatureFlag("chatlayerBotBuilderV2", isBoolean, false) &&
    isHiveFeatureOn(hiveConfig, "feature_chatbot"),
  menu: {
    icon: "branded-chatbot",
    textKey: "menuItemChatbot",
    href: "/chatbot/bots",
    subRoutes: [
      {
        key: getNewUniqueRouteKey("chatbot-flows"),
        path: "/chatbot/bots/:botId/*",
        onProjectChange: async () => history.pushState("", "", "/chatbot/bots"),
        textKey: "menuItemChatbotFlows",
        href: "/chatbot/bot/botdialogs/flows",
      },
      {
        key: getNewUniqueRouteKey("chatbot-intents"),
        path: "/chatbot/bot/NLP/intents",
        textKey: "menuItemChatbotIntents",
        href: "/chatbot/bot/NLP/intents",
      },
      {
        key: getNewUniqueRouteKey("chatbot-entities"),
        path: "/chatbot/bot/NLP/entities/*",
        textKey: "menuItemChatbotEntities",
        href: "/chatbot/bot/NLP/entities",
      },
      {
        key: getNewUniqueRouteKey("chatbot-knowledge-base"),
        path: "/chatbot/bot/kb",
        textKey: "menuItemChatbotKnowledgeBase",
        href: "/chatbot/bot/kb",
        isEnabled: () =>
          getFeatureFlag("enableKnowledgeBase", isBoolean, false),
      },
      {
        key: getNewUniqueRouteKey("chatbot-settings"),
        path: "/chatbot/bot/settings/bot",
        textKey: "menuItemChatbotSettings",
        href: "/chatbot/bot/settings/bot",
      },
    ],
  },
} satisfies RouteConfigMenu;

const campaignRoute = {
  key: getNewUniqueRouteKey("campaign"),
  path: "/campaigns/*",
  mfe: { name: "Campaigns", entry: "Container" },
  allowedPermissions: ["contentEdit"],
  isEnabled: ({ hiveConfig }) =>
    isHiveFeatureOn(hiveConfig, "feature_campaigns") &&
    hiveConfig?.feature_campaigns !== "campaigns",
  menu: {
    icon: "branded-campaigns",
    textKey: "menuItemCampaigns",
    href: "/campaigns/newsletters",
    endSection: true,
    subRoutes: [
      {
        key: getNewUniqueRouteKey("campaign-newsletter"),
        path: "/campaigns/newsletters/*",
        textKey: "menuItemNewsletter",
        href: "/campaigns/newsletters",
      },
      {
        key: getNewUniqueRouteKey("campaign-settings"),
        path: "/campaigns/settings/*",
        textKey: "menuItemCampaignsSettings",
        href: "/campaigns/settings",
        allowedPermissions: ["administrate"],
      },
    ],
  },
} satisfies RouteConfigMenu;

const newFlowCampaignsRoute = {
  key: getNewUniqueRouteKey("campaign"),
  path: "/campaigns/*",
  mfe: { name: "CampaignsCore", entry: "Container" },
  allowedPermissions: ["contentEdit"],
  isEnabled: ({ hiveConfig }) =>
    isHiveFeatureOn(hiveConfig, "feature_campaigns") &&
    hiveConfig?.feature_campaigns === "campaigns",
  menu: {
    icon: "branded-campaigns",
    textKey: "menuItemCampaigns",
    href: "/campaigns/all",
    endSection: true,
    subRoutes: [
      {
        key: getNewUniqueRouteKey("campaign-one-time"),
        path: "/campaigns/all/*",
        textKey: "menuItemCampaignsCore",
        href: "/campaigns/all",
      },
      {
        key: getNewUniqueRouteKey("campaign-analytics"),
        path: "/campaigns/analytics/*",
        textKey: "menuItemCampaignsAnalytics",
        href: "/campaigns/analytics",
      },
      {
        key: getNewUniqueRouteKey("campaign-settings"),
        mfe: { name: "Campaigns", entry: "Container" },
        path: "/campaigns/settings/*",
        textKey: "menuItemCampaignsSettings",
        href: "/campaigns/settings/settings",
        allowedPermissions: ["administrate"],
      },
    ],
  },
} satisfies RouteConfigMenu;

const analyticsRoute = {
  key: getNewUniqueRouteKey("analytics"),
  path: "/analytics/*",
  mfe: { name: "Analytics", entry: "Container" },
  allowedPermissions: ["statisticsChats"],
  menu: {
    icon: "branded-barchart-down",
    textKey: "menuItemAnalytics",
    href: "/analytics/my",
    subRoutes: [
      {
        key: getNewUniqueRouteKey("analytics-my"),
        path: "/analytics/my/*",
        textKey: "menuItemAnalyticsMy",
        href: "/analytics/my",
      },
      {
        key: getNewUniqueRouteKey("analytics-all"),
        path: "/analytics/all/*",
        textKey: "menuItemAnalyticsAll",
        href: "/analytics/all",
      },
    ],
  },
} satisfies RouteConfigMenu;

const customersRoute = {
  key: getNewUniqueRouteKey("customers"),
  path: "/customers/*",
  mfe: { name: "Customers", entry: "Container" },
  allowedPermissions: ["userShowList"],
  menu: {
    icon: "branded-users",
    textKey: "menuItemCustomers",
    href: "/customers",
    subRoutes: [
      {
        key: getNewUniqueRouteKey("customers-all"),
        path: "/customers",
        textKey: "menuItemCustomersAllCustomers",
        href: "/customers",
      },
      {
        key: getNewUniqueRouteKey("customers-groups"),
        path: "/customers/groups",
        textKey: "menuItemCustomersGroups",
        href: "/customers/groups",
        allowedPermissions: ["administrate"],
      },
      {
        key: getNewUniqueRouteKey("customers-properties"),
        path: "/customers/properties",
        textKey: "menuItemCustomersProperties",
        href: "/customers/properties",
        allowedPermissions: ["administrate"],
      },
    ],
  },
} satisfies RouteConfigMenu;

const loadShellTemplateManager = ({
  loadTemplateManagerMFE,
}: IsRouteEnabledOptions) =>
  !!loadTemplateManagerMFE &&
  getFeatureFlag("shellLoadTemplateManager", isBoolean, false);

const projectSettingsRoute = {
  key: getNewUniqueRouteKey("project-settings"),
  path: "/settings/project/*",
  mfe: { name: "Settings", entry: "Container" },
  allowedPermissions: ["administrate"],
  menu: {
    icon: "branded-settings",
    textKey: "subMenuProjectSettings",
    href: "/settings/project",
    subRoutes: [
      {
        key: getNewUniqueRouteKey("settings-project"),
        path: "/settings/project",
        textKey: "subMenuProjectSettingsProjectDetails",
        href: "/settings/project",
      },
      {
        key: getNewUniqueRouteKey("settings-channels"),
        path: "/settings/project/channels/*",
        textKey: "subMenuProjectSettingsChannels",
        mfe: { name: "Channels", entry: "Container" },
        href: "/settings/project/channels",
      },
      {
        key: getNewUniqueRouteKey("settings-promotion-tools"),
        path: "/settings/project/promotion-tools/*",
        textKey: "subMenuProjectSettingsPromotionTools",
        mfe: { name: "PromotionTools", entry: "Container" },
        href: "/settings/project/promotion-tools",
      },
      {
        key: getNewUniqueRouteKey("settings-api"),
        path: "/settings/project/api/*",
        textKey: "subMenuProjectSettingsAPI",
        mfe: { name: "Settings", entry: "Container" },
        href: "/settings/project/api",
      },
    ],
  },
} satisfies RouteConfigMenu;

const accountSettingsRoute = {
  key: getNewUniqueRouteKey("accountSettings"),
  path: "/settings/account/*",
  mfe: { name: "Settings", entry: "Container" },
  allowedPermissions: ["administrate"],
  menu: {
    icon: "branded-global-settings",
    textKey: "subMenuAccountSettings",
    href: "/settings/account/details",
    subRoutes: [
      {
        key: getNewUniqueRouteKey("account-settings"),
        path: "/settings/account/details/*",
        textKey: "subMenuAccountSettingsAccountDetails",
        mfe: { name: "AccountDetails", entry: "Container" },
        href: "/settings/account/details",
      },
      {
        key: getNewUniqueRouteKey("account-settings-team"),
        path: "/settings/account/team/*",
        textKey: "subMenuAccountSettingsTeam",
        mfe: { name: "Settings", entry: "Container" },
        href: "/settings/account/team",
      },
      {
        key: getNewUniqueRouteKey("account-settings-skills"),
        path: "/settings/account/skills/*",
        textKey: "subMenuAccountSettingsSkills",
        mfe: { name: "Settings", entry: "Container" },
        href: "/settings/account/skills",
      },
      {
        key: getNewUniqueRouteKey("account-settings-whats-app"),
        path: "/settings/account/whats-app/*",
        textKey: "subMenuAccountSettingsMessageTemplates",
        mfe: { name: "Settings", entry: "Container" },
        isEnabled: (options) => !loadShellTemplateManager(options),
        href: "/settings/account/whats-app",
      },
      {
        key: getNewUniqueRouteKey("account-settings-whats-appm"),
        path: "/settings/account/whats-app/*",
        textKey: "subMenuAccountSettingsWhatsAppMessageTemplate",
        mfe: { name: "TemplateManager", entry: "Container" },
        isEnabled: loadShellTemplateManager,
        href: "/settings/account/whats-app",
      },
    ],
  },
} satisfies RouteConfigMenu;

const billingRoute = {
  key: getNewUniqueRouteKey("billing"),
  path: "/settings/billing/*",
  mfe: { name: "Billing", entry: "Container" },
  allowedPermissions: ["administrate"],
  menu: {
    icon: "branded-finance",
    textKey: "subMenuBilling",
    href: "/settings/billing/contract",
    subRoutes: [
      {
        key: getNewUniqueRouteKey("billing-contract"),
        path: "/settings/billing/contract/*",
        textKey: "subMenuBillingContract",
        href: "/settings/billing/contract",
      },
      {
        key: getNewUniqueRouteKey("billing-invoices"),
        path: "/settings/billing/invoices/*",
        textKey: "subMenuBillingInvoices",
        href: "/settings/billing/invoices",
      },
    ],
  },
} satisfies RouteConfigMenu;

const personalisationRoute = {
  key: getNewUniqueRouteKey("personalisation"),
  path: "/personalise/*",
  mfe: { name: "Personalisation", entry: "MainEntry" },
  allowedPermissions: ["administrate"],
  hideUi: true,
} satisfies RouteConfigBase;

const profileRoute = {
  key: getNewUniqueRouteKey("profile"),
  path: "/settings/profile/*",
  mfe: { name: "Settings", entry: "Container" },
  menu: {
    icon: "branded-user",
    textKey: "subMenuProfile",
    href: "/settings/profile",
  },
} satisfies RouteConfigMenu;

const purchaseRoute = {
  key: getNewUniqueRouteKey("purchase-flow"),
  path: "/purchase/*",
  mfe: { name: "PurchaseFlow", entry: "MainEntry" },
  allowedPermissions: ["administrate"],
} satisfies RouteConfigBase;

/** Main Menu configuration. Order matters. */
export const routesConfig: RouteConfigMenu[] = [
  homeRoute,
  conversationsRoute,
  chatbotRoute,
  chatbotV2Route,
  campaignRoute,
  newFlowCampaignsRoute,
  analyticsRoute,
  customersRoute,
];

/** Settings Menu configuration. Order matters. */
export const settingsRoutesConfig: RouteConfigMenu[] = [
  projectSettingsRoute,
  accountSettingsRoute,
  billingRoute,
  profileRoute,
];

export const loggedInRoutesConfig: RouteConfig[] = [
  ...routesConfig,
  ...settingsRoutesConfig,
  purchaseRoute,
  personalisationRoute,
];

export const nonLoggedInRoutesConfig: RouteConfig[] = [
  {
    path: "/signup/*",
    key: getNewUniqueRouteKey("signup"),
    mfe: { name: "SignUp", entry: "MainEntry" },
    hideUi: true,
  } satisfies RouteConfigBase,
  ...[
    "/forgot-password",
    "/reset-password",
    "/second-login",
    "/just-the-loader",
    "/missing-browser-support",
  ].map(
    (path) =>
      ({
        path,
        key: getNewUniqueRouteKey(path),
        mfe: { name: "EngageUI", entry: "MainEntry" },
        hideUi: true,
      } satisfies RouteConfigBase)
  ),
];

export const allRoutesConfigs = [
  ...loggedInRoutesConfig,
  ...nonLoggedInRoutesConfig,
];

// These are used elsewhere in the codebase for linking and preloading.
export {
  profileRoute,
  conversationsRoute,
  campaignRoute,
  conversationsWorkspaceSubRoute,
  conversationsAssignmentSubRoute,
};

export const preloadRoute = (
  pathname: string,
  routes: readonly (RouteConfig | RouteConfigSubMenu)[]
) => {
  routes.forEach(async (route) => {
    try {
      if ("href" in route && route.href === pathname && route.mfe) {
        loadMFE(route.mfe?.name, route.mfe?.entry);
      } else if (
        route.mfe &&
        route.path.endsWith("/*") &&
        pathname.startsWith(route.path.slice(0, -1))
      ) {
        await loadMFE(route.mfe?.name, route.mfe?.entry);
      }

      if ("menu" in route && "subRoutes" in route.menu) {
        preloadRoute(pathname, route.menu.subRoutes);
      }
    } catch (error) {
      console.error("IMPORT_ERROR", error);
    }
  });
};
